import { useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Paperclip, X, Loader2, Check } from "lucide-react";
import { useT } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EXT = ["pdf", "jpg", "jpeg", "png", "dwg", "dxf", "zip"];
const MAX_FILES = 3;
const MAX_BYTES = 10 * 1024 * 1024;
const fmt = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

const FileRow = ({ f, onRemove, i }) => (
  <li className="flex items-center gap-3 border-b border-[#A39E93]/25 py-2 text-sm" data-testid={`attachment-item-${i}`}>
    {f.id ? <Check className="h-4 w-4 shrink-0 text-[#D98A52]" /> : <Loader2 className="h-4 w-4 shrink-0 animate-spin text-[#D98A52]" />}
    <span className="min-w-0 flex-1 truncate">{f.name}</span>
    <span className="shrink-0 font-mono text-xs opacity-70">{f.id ? fmt(f.size) : `${f.progress}%`}</span>
    <button type="button" onClick={() => onRemove(f.key)} className="shrink-0 rounded-full p-1 transition-colors hover:bg-[#D98A52]/20" aria-label="Rimuovi" data-testid={`attachment-remove-${i}`}>
      <X className="h-4 w-4" />
    </button>
  </li>
);

export const QuoteAttachments = ({ files, setFiles }) => {
  const t = useT();
  const input = useRef(null);
  const update = (key, patch) => setFiles((list) => list.map((f) => (f.key === key ? { ...f, ...patch } : f)));

  const upload = async (file) => {
    const key = `${file.name}-${Date.now()}-${Math.random()}`;
    setFiles((list) => [...list, { key, name: file.name, size: file.size, progress: 0, id: null }]);
    const body = new FormData();
    body.append("file", file);
    try {
      const res = await axios.post(`${API}/quotes/attachments`, body, {
        onUploadProgress: (e) => update(key, { progress: e.total ? Math.round((e.loaded / e.total) * 100) : 0 }),
      });
      update(key, { id: res.data?.id ?? null, progress: 100 });
    } catch {
      setFiles((list) => list.filter((f) => f.key !== key));
      toast.error(t("att.errUpload", "Caricamento non riuscito: ") + file.name);
    }
  };

  const onPick = (e) => {
    const picked = Array.from(e.target.files ?? []);
    e.target.value = "";
    addFiles(picked);
  };

  const addFiles = (picked) => {
    let room = MAX_FILES - files.length;
    picked.forEach((file) => {
      const ext = file.name.split(".").pop().toLowerCase();
      if (!EXT.includes(ext)) return toast.error(t("att.errType", "Formato non consentito: ") + file.name);
      if (file.size > MAX_BYTES) return toast.error(t("att.errSize", "File oltre 10 MB: ") + file.name);
      if (room <= 0) return toast.error(t("att.errMax", "Puoi allegare al massimo 3 file"));
      room -= 1;
      upload(file);
    });
  };

  useEffect(() => {
    const onAttach = (e) => addFiles(Array.isArray(e?.detail) ? e.detail : []);
    window.addEventListener("quote-attach-files", onAttach);
    return () => window.removeEventListener("quote-attach-files", onAttach);
  });

  return (
    <div className="sm:col-span-2" data-testid="quote-attachments">
      <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#A39E93]">{t("att.label", "Allegati (facoltativo)")}</span>
      <input ref={input} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.dwg,.dxf,.zip" className="hidden" onChange={onPick} data-testid="attachment-input" />
      <button
        type="button"
        onClick={() => input.current?.click()}
        disabled={files.length >= MAX_FILES}
        className="mt-3 flex w-full items-center gap-3 border border-dashed border-[#A39E93]/50 px-4 py-4 text-left text-sm transition-colors hover:border-[#D98A52] hover:text-[#D98A52] disabled:cursor-not-allowed disabled:opacity-50"
        data-testid="attachment-add-btn"
      >
        <Paperclip className="h-4 w-4 shrink-0" />
        <span>
          {t("att.add", "Aggiungi progetti, disegni o foto")}
          <span className="block text-xs opacity-70">{t("att.hint", "PDF, JPG, PNG, DWG, DXF, ZIP · max 3 file da 10 MB")}</span>
        </span>
      </button>
      {files.length > 0 && <ul className="mt-2">{files.map((f, i) => <FileRow key={f.key} f={f} i={i} onRemove={(k) => setFiles((l) => l.filter((x) => x.key !== k))} />)}</ul>}
    </div>
  );
};
